import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';
import {
    campaignHostCheckingOutSelector,
    campaignHostLtdPromoActiveSelector,
    campaignHostLtdPromotionSelector,
    campaignHostPackagesSelector,
    campaignHostFetchingPricingSelector,
} from '../../../redux/campaignHostSubscription/selectors';
import { isCampaignHostEnterprisePackage, isCampaignHostLtdPackage } from '../../../utils/campaignHostSubscriptionApi';
import { formatTierPackagePrice, packagePriceSuffix } from '../../../utils/campaignHostSubscriptionFormat';
import type { ICampaignHostPackage } from '../../../types/global/campaignHostSubscription.types';
import { getTierFeatureRows, type CampaignHostTierKind } from './campaignHostTierFeatures';
import { CampaignHostSuiteFeatureList } from './CampaignHostSuiteFeatureList';
import { CampaignHostLtdPromoCountdown } from './CampaignHostLtdPromoCountdown';
import { CampaignHostEnterpriseContactDialog } from './CampaignHostEnterpriseContactDialog';
import { fetchCampaignHostPricing } from '../../../redux/campaignHostSubscription/actions';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../../analytics/Mixpanel';

const GOLD = '#FFC107';
const GOLD_GLOW = '0 0 25px rgba(255, 193, 7, 0.5)';
const MODAL_Z_INDEX = 1500;

function packageTierKind(pkg: ICampaignHostPackage): CampaignHostTierKind {
    if (isCampaignHostLtdPackage(pkg)) return 'ltd';
    if (isCampaignHostEnterprisePackage(pkg)) return 'enterprise';
    return 'monthly';
}

/** Choose Your Tier — brand campaign host subscription (controlled by parent). */
export interface CampaignHostTierPickerModalProps {
    open: boolean;
    onClose: () => void;
    onSelectMonthly: (pkg: ICampaignHostPackage) => void;
    onSelectLtd: (pkg: ICampaignHostPackage) => void;
}

export function CampaignHostTierPickerModal({
    open,
    onClose,
    onSelectMonthly,
    onSelectLtd,
}: CampaignHostTierPickerModalProps) {
    const dispatch = useDispatch();
    const packages = useSelector(campaignHostPackagesSelector);
    const ltdPromotion = useSelector(campaignHostLtdPromotionSelector);
    const ltdPromoActive = useSelector(campaignHostLtdPromoActiveSelector);
    const checkingOut = useSelector(campaignHostCheckingOutSelector);
    const loadingPricing = useSelector(campaignHostFetchingPricingSelector);
    const muiTheme = useTheme();
    const fullScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [enterpriseContactOpen, setEnterpriseContactOpen] = useState(false);

    const sortedPackages = useMemo(
        () => [...packages].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
        [packages],
    );

    /** Lifetime tier only when ltd_promotion.enabled && active (running promo). */
    const visiblePackages = useMemo(
        () =>
            sortedPackages.filter(pkg => {
                if (isCampaignHostLtdPackage(pkg)) {
                    return ltdPromoActive;
                }
                return true;
            }),
        [sortedPackages, ltdPromoActive],
    );

    useEffect(() => {
        if (open) dispatch(fetchCampaignHostPricing());
    }, [open, dispatch]);

    useEffect(() => {
        if (!open) setEnterpriseContactOpen(false);
    }, [open]);

    const plansLoading = open && loadingPricing;
    const checkoutBlocked = checkingOut || loadingPricing;
    const columnCount = visiblePackages.length;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={fullScreen}
                maxWidth="md"
                fullWidth
                sx={{ zIndex: MODAL_Z_INDEX }}
                slotProps={{
                    backdrop: { sx: { zIndex: MODAL_Z_INDEX - 1 } },
                }}
                PaperProps={{
                    sx: {
                        zIndex: MODAL_Z_INDEX,
                        bgcolor: 'rgba(38, 38, 38, 0.6)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}1a`,
                        borderRadius: fullScreen ? 0 : 6,
                        p: { xs: 3, md: 6 },
                        maxWidth: columnCount >= 3 ? 1100 : 920,
                        boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                    },
                }}>
                <IconButton onClick={onClose} sx={{ position: 'absolute', top: 16, right: 16, color: '#adaaaa' }}>
                    <CloseIcon />
                </IconButton>

                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 800,
                            fontSize: { xs: 28, md: 40 },
                            color: BRAND_LUXE_PRIMARY,
                            letterSpacing: '-0.02em',
                            mb: 1.5,
                        }}>
                        Choose Your Tier
                    </Typography>
                    <Typography sx={{ color: '#adaaaa', fontSize: 17, maxWidth: 480, mx: 'auto', mb: 2 }}>
                        Elevate your content creation with premium tools designed for the next generation of curators.
                    </Typography>
                    <CampaignHostLtdPromoCountdown
                        endsAt={ltdPromotion?.ends_at}
                        show={ltdPromoActive}
                        onComplete={() => dispatch(fetchCampaignHostPricing())}
                    />
                </Box>

                {plansLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : visiblePackages.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', color: '#adaaaa', py: 6 }}>
                        Plans are unavailable right now. Please try again in a moment.
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: columnCount >= 3 ? '1fr 1fr 1fr' : columnCount > 1 ? '1fr 1fr' : '1fr',
                            },
                            gap: 3,
                            mb: 4,
                        }}>
                        {visiblePackages.map(pkg => {
                            const kind = packageTierKind(pkg);
                            const isLtd = kind === 'ltd';
                            const isEnterprise = kind === 'enterprise';
                            return (
                                <TierCard
                                    key={pkg._id}
                                    title={pkg.name}
                                    price={formatTierPackagePrice(pkg, {
                                        ltdPromoActive: ltdPromoActive && isLtd,
                                        promoPriceCents: ltdPromotion?.promo_price_cents ?? null,
                                    })}
                                    suffix={packagePriceSuffix(pkg)}
                                    featured={isLtd}
                                    badge={isLtd ? 'Best Value' : undefined}
                                    tierKind={kind}
                                    ctaLabel={
                                        isLtd
                                            ? 'Claim Lifetime Access'
                                            : isEnterprise
                                            ? 'Contact Support'
                                            : 'Get Started'
                                    }
                                    disabled={isEnterprise ? false : checkoutBlocked}
                                    onCta={() => {
                                        const ctaLabel = isLtd
                                            ? 'Claim Lifetime Access'
                                            : isEnterprise
                                            ? 'Contact Support'
                                            : 'Get Started';
                                        trackSubscriptionAction(MIXPANEL_EVENT.SELECT_SUBSCRIPTION_PLAN_CLICK, {
                                            buttonName: ctaLabel.toUpperCase(),
                                            buttonAction: 'SELECT_SUBSCRIPTION_PLAN',
                                            subscriptionType: 'campaign_host',
                                            packageId: pkg._id ?? pkg.id ?? null,
                                            packageName: pkg.name,
                                            tierKind: kind,
                                        });
                                        if (isEnterprise) {
                                            setEnterpriseContactOpen(true);
                                            return;
                                        }
                                        if (isLtd) {
                                            onSelectLtd(pkg);
                                            return;
                                        }
                                        onSelectMonthly(pkg);
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <VerifiedUserIcon sx={{ color: BRAND_LUXE_SECONDARY, fontSize: 20 }} />
                        <Typography sx={{ color: '#adaaaa', fontSize: 14 }}>
                            Safe & Secure Checkout. Cancel monthly anytime.
                        </Typography>
                    </Box>
                </Box>
            </Dialog>

            <CampaignHostEnterpriseContactDialog
                open={enterpriseContactOpen}
                onClose={() => setEnterpriseContactOpen(false)}
                onSubmitted={onClose}
            />
        </>
    );
}

function TierCard({
    title,
    price,
    suffix,
    featured,
    badge,
    tierKind,
    ctaLabel,
    disabled,
    onCta,
}: {
    title: string;
    price: string;
    suffix: string;
    featured?: boolean;
    badge?: string;
    tierKind: CampaignHostTierKind;
    ctaLabel: string;
    disabled?: boolean;
    onCta: () => void;
}) {
    const featureRows = getTierFeatureRows(tierKind);
    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                p: 4,
                borderRadius: 8,
                bgcolor: featured ? BRAND_SURFACE_CONTAINER_HIGH : BRAND_SURFACE_CONTAINER_LOW,
                border: featured ? `2px solid ${GOLD}` : `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                boxShadow: featured ? GOLD_GLOW : 'none',
                transform: featured ? { md: 'scale(1.03)' } : 'none',
                minHeight: 380,
            }}>
            {badge && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY} 0%, #feb700 100%)`,
                        color: '#392700',
                        px: 2,
                        py: 0.5,
                        borderRadius: 99,
                        fontSize: 10,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        letterSpacing: '0.12em',
                    }}>
                    {badge}
                </Box>
            )}
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: featured ? GOLD : '#fff', mb: 1 }}>
                {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 3 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 32, color: '#fff' }}>{price}</Typography>
                <Typography sx={{ color: '#adaaaa', fontSize: 12, textTransform: 'uppercase' }}>{suffix}</Typography>
            </Box>
            <Box sx={{ flex: 1, mb: 3 }}>
                <CampaignHostSuiteFeatureList rows={featureRows} variant="tier" featured={featured} />
            </Box>
            <Button
                fullWidth
                type="button"
                disabled={disabled}
                onClick={onCta}
                sx={{
                    py: 1.75,
                    borderRadius: 99,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontWeight: 800,
                    textTransform: 'none',
                    fontSize: 16,
                    ...(featured
                        ? {
                              background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY} 0%, #feb700 100%)`,
                              color: '#392700',
                              boxShadow: GOLD_GLOW,
                          }
                        : {
                              border: `1px solid ${GOLD}66`,
                              color: GOLD,
                              bgcolor: 'transparent',
                          }),
                }}>
                {ctaLabel}
            </Button>
        </Box>
    );
}

/** Alias for product naming */
export const CampaignHostSubscriptionModal = CampaignHostTierPickerModal;
